import {createContext, forwardRef, PropsWithChildren, useMemo} from "react";
import {useUser} from "../../../../misc/Permission";
import {Opening} from "../../../../api/Openings";
import {DrawingInfo, ExportColumn} from "./MakeSubmittal";
import {PageRenderProvider} from "../../../../misc/pdf/parts/PageContext";
import {PDF} from "../../../../misc/pdf/parts/PDF";
import {AbbreviationPage} from "./pdf/AbbreviationPage";
import {OpeningsPage} from "./pdf/OpeningsPage";
import {Project} from "../../../../api/Projects";
import {Company} from "../../../../api/Companies";
import {CoverPage} from "../../../../misc/pdf/parts/CoverPage";
import {ContainerPage} from "../../../../misc/pdf/parts/ContainerPage";
import {useReadyCheck} from "../../../../misc/pdf/UseReadyCheck";
import {TitleTextBlock} from "../../../../misc/pdf/parts/TitleTextBlock";
import {ImageTilePage} from "./ImageTilePage";
import {QuoteOpening} from "../../../../api/QuoteOpenings";
import {PageItem} from "./pdf/PagesTab";
import {Contact} from "../../../../api/Contacts";
import {DoorElevationAndLiteKitProfilesPages} from "./pdf/DoorElevationAndLiteKitProfilesPages";
import {GlazingSection, GlazingSectionPage} from "../glazingSubmittal/GlazingSectionPage";
import {preambleToBlocks} from "../../../../misc/richtext/preambleContent";

// a single drw elevation-tool rendering for the submittal grid: the drawing image plus its
// label ("STD-A"), which prints as a text footer under the tile.
export type ElevationRendering = {
    image: string;
    label: string;
};

export const OpeningsPDF = forwardRef(function(props: {
    project: Project;
    client?: Company;
    clientContact?: Contact;
    data: (Opening|QuoteOpening)[]
    columns: ExportColumn[]
    preamble: string;
    drawingInfo: DrawingInfo;
    lastSubmittal: string|null;
    screenElevations: string[];
    doorElevations: {
        name: string;
        image: string;
        profile: string | null;
    }[];
    doorElevationsSimple: string[]
    // the drw elevation-tool renderings (one image per distinct rendering), drawn as a grid
    // beside the legacy elevations for screen/door; hardware has no legacy counterpart. The
    // label ("STD-A") prints as a text footer per tile — it is no longer baked into the image.
    screenRenderings: ElevationRendering[];
    doorRenderings: ElevationRendering[];
    hardwareRenderings: ElevationRendering[];
    liteKitProfiles: string[];
    // the glass each frame/door elevation variant needs, in the same form the glazing submittal
    // prints. Empty unless the lite kit page is picked.
    glazingScreens: GlazingSection[];
    glazingDoors: GlazingSection[];
    anchorImages: string[];
    frameProfileImages: string[];
    pages: PageItem[];
    splitByPhase: boolean;
    onReady(): void;
}, ref: any) {
    const usr = useUser();
    const ready = useReadyCheck(props.onReady);
    ready.require(!!usr);

    const preamble = useMemo(() => preambleToBlocks(props.preamble || ""), [props.preamble])

    return (
        <div ref={ref}>
            <PDF>
                <PDFProjectContext.Provider value={props.project}>
                    <ClientInfoContextProvider client={props.client} clientContact={props.clientContact}>
                        <CoverPage
                            title="DOOR & FRAME SHOP DRAWINGS"
                            lastSubmittal={props.lastSubmittal}
                        />
                        {props.pages.includes("preamble") && <PageRenderProvider data={preamble} onReady={ready.section()}>
                            {info =>
                                <ContainerPage>
                                    <TitleTextBlock title="PREAMBLE">
                                        {info.rows.map((d, index) => <div key={index.toString()} dangerouslySetInnerHTML={{__html: d}} />)}
                                    </TitleTextBlock>
                                </ContainerPage>}
                        </PageRenderProvider>}
                        {props.pages.includes("abbreviations") && <AbbreviationPage drawingInfo={props.drawingInfo} onReady={ready.section()} />}
                        {props.pages.includes("openings") && <OpeningsPage columns={props.columns} data={props.data} splitByPhase={props.splitByPhase} onReady={ready.section()} drawingInfo={props.drawingInfo} />}
                        {props.pages.includes("frameProfiles") && <ImageTilePage title={"FRAME PROFILES"} images={props.frameProfileImages} />}
                        {props.pages.includes("anchorElevations") && <ImageTilePage title={"ANCHOR ELEVATIONS"} images={props.anchorImages} />}
                        {props.pages.includes("screenElevations") && <ImageTilePage title={"SCREEN ELEVATIONS"} images={props.screenElevations} />}
                        {props.pages.includes("screenElevations") && <ImageTilePage title={"SCREEN ELEVATIONS"} images={props.screenRenderings.map(r => r.image)} labels={props.screenRenderings.map(r => r.label)} highRes />}
                        {props.pages.includes("doorElevations") && !props.pages.includes("liteKitProfiles") && <ImageTilePage title={"DOOR ELEVATIONS"} images={props.doorElevationsSimple} />}
                        {!props.pages.includes("doorElevations") && props.pages.includes("liteKitProfiles") && <ImageTilePage title={"LITE KIT PROFILES"} images={props.liteKitProfiles} />}
                        {props.pages.includes("doorElevations") && props.pages.includes("liteKitProfiles") &&
                            <DoorElevationAndLiteKitProfilesPages
                                title={"DOOR ELEVATIONS & LITE KIT PROFILES"}
                                doorElevations={props.doorElevations}
                                onReady={ready.section()}
                            />}
                        {props.pages.includes("doorElevations") && <ImageTilePage title={"DOOR ELEVATIONS"} images={props.doorRenderings.map(r => r.image)} labels={props.doorRenderings.map(r => r.label)} highRes />}
                        {props.pages.includes("liteKitProfiles") && props.glazingScreens.map((s, index) => (
                            <GlazingSectionPage key={"glazing-screen-" + index} title="FRAME GLAZING" section={s} />
                        ))}
                        {props.pages.includes("liteKitProfiles") && props.glazingDoors.map((s, index) => (
                            <GlazingSectionPage key={"glazing-door-" + index} title="DOOR GLAZING" section={s} />
                        ))}
                        {props.pages.includes("hardwareElevations") && <ImageTilePage title={"HARDWARE ELEVATIONS"} images={props.hardwareRenderings.map(r => r.image)} labels={props.hardwareRenderings.map(r => r.label)} highRes />}
                    </ClientInfoContextProvider>
                </PDFProjectContext.Provider>
            </PDF>
        </div>
    )
});

export const PDFProjectContext = createContext<Project>({} as Project);
export const ClientInfoContext = createContext({client: undefined as Company|undefined, clientContact: undefined as Contact|undefined});

export function ClientInfoContextProvider(props: PropsWithChildren<{
    client: Company|undefined;
    clientContact: Contact|undefined;
}>) {
    const ctx = useMemo(() => ({
        client: props.client,
        clientContact: props.clientContact,
    }), [props.client, props.clientContact]);

    return (
        <ClientInfoContext.Provider value={ctx}>
            {props.children}
        </ClientInfoContext.Provider>
    );
}